/**
 * Centralized formatting utilities for consistent display across the application.
 *
 * Standards:
 * - Currency Compact: $2.35M, $150K (for MetricCard tiles)
 * - Currency Full: $47,500,000 (for tables, tooltips)
 * - Percentage: 75.0% (1 decimal by default)
 * - Team Weeks: 48 TW or 48.5 TW
 */

// Cached formatters for performance
const fullCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('en-US');

/**
 * Format currency in compact form for dashboard tiles and metrics.
 *
 * Output format:
 * - ≥$1T: $1.2T (1 decimal)
 * - ≥$1B: $1.2B (1 decimal)
 * - ≥$1M: $2.35M (2 decimals)
 * - ≥$100K: $150K (0 decimals)
 * - ≥$1K: $1.5K (1 decimal)
 * - <$1K: $500 (0 decimals)
 *
 * Edge cases:
 * - NaN, Infinity, or values > 999T return fallback
 */
export function formatCurrencyCompact(
  value: number | null | undefined,
  options?: { showSign?: boolean; fallback?: string }
): string {
  const fallback = options?.fallback ?? '$0';
  if (value == null) return fallback;

  // Handle NaN, Infinity, and unreasonably large values
  if (!Number.isFinite(value) || Math.abs(value) > 999_999_999_999_999) {
    return fallback;
  }

  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : (options?.showSign && value > 0 ? '+' : '');

  if (abs >= 1_000_000_000_000) return `${sign}$${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 100_000) return `${sign}$${Math.round(abs / 1e3)}K`;
  if (abs >= 1_000) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(abs)}`;
}

/**
 * Format currency in full form with commas for tables and detailed views.
 *
 * Output format: $47,500,000
 *
 * Edge cases:
 * - NaN, Infinity return fallback
 */
export function formatCurrencyFull(
  value: number | null | undefined,
  options?: { fallback?: string }
): string {
  const fallback = options?.fallback ?? 'Not Set';
  if (value == null || !Number.isFinite(value)) return fallback;
  return fullCurrencyFormatter.format(value);
}

/**
 * Format percentage with configurable decimal places.
 *
 * Output format: 75.0% (default 1 decimal)
 */
export function formatPercent(
  value: number | null | undefined,
  options?: { decimals?: number; showSign?: boolean; fallback?: string }
): string {
  const { decimals = 1, showSign = false, fallback = 'N/A' } = options ?? {};
  if (value == null) return fallback;
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

/**
 * Format percentage for trend indicators with +/- sign.
 *
 * Output format: +12.5% or -3.2%
 */
export function formatPercentTrend(
  value: number | null | undefined,
  options?: { decimals?: number; fallback?: string }
): string {
  return formatPercent(value, { ...options, showSign: true });
}

/**
 * Format number with commas for readability.
 *
 * Output format: 1,234,567
 */
export function formatNumber(
  value: number | null | undefined,
  options?: { decimals?: number; fallback?: string }
): string {
  const { decimals = 0, fallback = '0' } = options ?? {};
  if (value == null) return fallback;

  if (decimals === 0) {
    return numberFormatter.format(Math.round(value));
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format team weeks capacity metric.
 *
 * Output format: 48 TW or 48.5 TW (shows decimal only when needed)
 */
export function formatTeamWeeks(
  value: number | null | undefined,
  options?: { fallback?: string; full?: boolean }
): string {
  if (value == null) return options?.fallback ?? '0 TW';
  const formatted = Number.isInteger(value) ? value.toString() : value.toFixed(1);
  const unit = options?.full ? ' Team Weeks' : ' TW';
  return `${formatted}${unit}`;
}

/**
 * Format a raw number for display in compact form (K, M, B, T suffixes).
 * Similar to formatCurrencyCompact but without the $ sign.
 *
 * Output format: 1.2T, 1.2B, 2.35M, 150K, 1.5K, 500
 *
 * Edge cases:
 * - NaN, Infinity, or values > 999T return fallback
 */
export function formatNumberCompact(
  value: number | null | undefined,
  options?: { showSign?: boolean; fallback?: string }
): string {
  const fallback = options?.fallback ?? '0';
  if (value == null) return fallback;

  // Handle NaN, Infinity, and unreasonably large values
  if (!Number.isFinite(value) || Math.abs(value) > 999_999_999_999_999) {
    return fallback;
  }

  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : (options?.showSign && value > 0 ? '+' : '');

  if (abs >= 1_000_000_000_000) return `${sign}${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 100_000) return `${sign}${Math.round(abs / 1e3)}K`;
  if (abs >= 1_000) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${Math.round(abs)}`;
}
